import { getCompilerLanguage } from './compilerLanguages';

const COMMON_FEATURES = [
  'Run code instantly in the browser — no install',
  'Custom stdin in the Test Cases tab',
  'Syntax highlighting and shareable code links',
  'Switch between 11 languages on CrafterHack IDE',
];

const COMMON_FAQ = (label) => [
  {
    q: `Is this ${label} compiler free?`,
    a: 'Yes. CrafterHack IDE is free to use for writing, running, and sharing code online.',
  },
  {
    q: 'Do I need to create an account?',
    a: 'No account is required to run code. Sign in to save progress on problems and contests.',
  },
  {
    q: 'Can I share my code with others?',
    a: 'Use Share in the toolbar to generate a link others can open in the IDE.',
  },
];

/** Per-language SEO overrides (optional). Falls back to templates. */
const SEO_OVERRIDES = {
  python: {
    intro:
      'Run Python 3 online with stdin support — ideal for scripts, interview prep, and quick experiments. Write in the editor, add input in Test Cases, and hit Run.',
    keywords: 'online python compiler, python interpreter online, run python code',
  },
  java: {
    intro:
      'Compile and run Java online with a ready-made Main class template. Practice DSA, university assignments, or interview questions without setting up JDK locally.',
    keywords: 'online java compiler, run java online, java compiler and executor',
  },
  javascript: {
    intro:
      'Run JavaScript with Node.js in the browser. Test algorithms, JSON parsing, and stdin-based solutions — then share a link with your code.',
    keywords: 'online javascript compiler, node js online, run javascript online',
  },
  cpp: {
    intro:
      'Compile and execute C++ online with GCC. Use custom input for competitive programming style problems and see output in seconds.',
    keywords: 'online c++ compiler, cpp online compiler, run c++ online',
  },
  go: {
    intro:
      'Write and run Go programs online — great for learning concurrency patterns, CLI tools, and interview prep with fast compile feedback.',
    keywords: 'online go compiler, golang online, run go code online',
  },
  rust: {
    intro:
      'Try Rust in the browser with a starter main template. Experiment with ownership-friendly code and run with custom stdin.',
    keywords: 'online rust compiler, rust playground online',
  },
  typescript: {
    intro:
      'Run TypeScript online (transpiled via Judge0). Prototype types, practice interviews, and share snippets with teammates.',
    keywords: 'online typescript compiler, run typescript online',
  },
};

export const getIdeLanguageSeo = (languageId) => {
  const lang = getCompilerLanguage(languageId);
  const override = SEO_OVERRIDES[lang.id] || {};

  const label = lang.label.replace(/\s*\(.*\)/, '').trim();

  return {
    h1: `Online ${label} Compiler`,
    title: `Online ${label} Compiler & Editor | CrafterHack`,
    description:
      override.intro?.slice(0, 140) ||
      `Free online ${label} compiler on CrafterHack. Write, run, and share ${label} code in your browser with stdin support and syntax highlighting.`,
    intro:
      override.intro ||
      `Write and run ${label} code online on CrafterHack. Use the editor below, add input in Test Cases, and press Run — or ⌘/Ctrl+Enter.`,
    keywords:
      override.keywords ||
      `online ${label.toLowerCase()} compiler, run ${label.toLowerCase()} online, ${label.toLowerCase()} editor`,
    features: override.features || COMMON_FEATURES,
    faq: override.faq || COMMON_FAQ(label),
    path: `/ide/${lang.id}`,
    canonical: `https://crafterhack.com/ide/${lang.id}`,
  };
};
